import React from "react";
import { formatDecimal } from "@globals/format";
import { t } from "@client/lib/dictionary";
import type { TracerDeviceInfo, TracerListItem } from "@client/types/tracer";
import { Card, CardContent, CardHeader, CardTitle } from "@client/components/ui/card";

interface Props {
  record: TracerListItem | undefined;
  deviceInfo: TracerDeviceInfo | null | undefined;
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-border/50 last:border-0">
    <span className="text-muted-foreground text-sm">{label}</span>
    <span className="font-mono text-sm text-right">{value || "—"}</span>
  </div>
);

const fmt = (n: number, digits: number = 1) => formatDecimal(Number(n), digits);

export const TracerDeviceInfoCard: React.FC<Props> = ({ record, deviceInfo }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.tracer.detail.deviceInfo}</CardTitle>
      </CardHeader>
      <CardContent>
        {deviceInfo && (
          <>
            <Row label="Rated PV V" value={`${fmt(deviceInfo.ratedPvVoltageV)} V`} />
            <Row label="Rated PV A" value={`${fmt(deviceInfo.ratedPvCurrentA)} A`} />
            <Row label="Rated PV W" value={`${fmt(deviceInfo.ratedPvPowerW)} W`} />
            <Row label="Rated baterie V" value={`${fmt(deviceInfo.ratedBatVoltageV)} V`} />
            <Row label="Rated nabíjecí A" value={`${fmt(deviceInfo.ratedBatChargeCurrentA)} A`} />
            <Row label="Rated nabíjecí W" value={`${fmt(deviceInfo.ratedBatChargePowerW)} W`} />
            {deviceInfo.ratedLoadCurrentA !== null && (
              <Row label="Rated load A" value={`${fmt(deviceInfo.ratedLoadCurrentA)} A`} />
            )}
            <Row label="Detekované nominální V" value={`${fmt(deviceInfo.detectedBatteryNominalV)} V`} />
          </>
        )}
        {record && (
          <>
            <Row label="Slug" value={record.slug} />
            <Row label="Serial port" value={record.transportTarget} />
            <Row label="Modbus slave ID" value={record.slaveId} />
            <Row label="Baud rate" value={`${record.baudRate} 8N1`} />
            <Row
              label="Typ baterie"
              value={
                t.tracer.batteryType[record.batteryType as keyof typeof t.tracer.batteryType] ?? record.batteryType
              }
            />
            <Row label="Nominální V (config)" value={`${record.batteryNominalV} V`} />
            {record.batteryCapacityAh !== null && (
              <Row label="Kapacita (config)" value={`${record.batteryCapacityAh} Ah`} />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
